import type { VarlessCopyId } from '@npc/copy'

import type { DevelopmentTaskPreviewState } from '../../app/development-tasks'
import { ApiFailure } from '../../app/errors'
import type { TagTone } from '../../components/tag'

/**
 * 快速预览面板背后的判据：哪一档说哪句话、点不了是哪一种、没交成卡在谁那儿。
 *
 * 和渲染分开放，不是为了洁癖——这一层不碰 React，用例可以直接喂一组值验它，
 * 不用先渲染一棵树。渲染那边只剩「把判据的结果摆出来」。
 *
 * 预览与发布是两条通道：预览只认「AI 交上来的这一版」和任务冻结时的 packet digest，
 * 跑在一次性的隔离环境里，没跑通自己回滚；发布那条要独立复核与批准，不从这里走。
 * 这一屏只说得出服务端说过的话——回执里没有的东西（比如预览地址）这里不编。
 *
 * 两张表都是 `Record<DevelopmentTaskPreviewState, …>`：契约里加一档而这里没跟上，
 * `pnpm typecheck` 当场红。写成 `Record<string, …>` 就没有这道保证了。
 */
export const stateCopy: Record<DevelopmentTaskPreviewState, VarlessCopyId> = {
  owner_tip: 'development_task.preview.state.owner_tip',
  preview_ready: 'development_task.preview.state.preview_ready',
  preview_running: 'development_task.preview.state.preview_running',
  test_candidate: 'development_task.preview.state.test_candidate',
  preview_failed_rolled_back: 'development_task.preview.state.preview_failed_rolled_back',
  release_requested: 'development_task.preview.state.release_requested',
  production_verified: 'development_task.preview.state.production_verified',
}

export const stateTone: Record<DevelopmentTaskPreviewState, TagTone> = {
  owner_tip: 'neutral',
  preview_ready: 'info',
  preview_running: 'info',
  test_candidate: 'ok',
  preview_failed_rolled_back: 'danger',
  release_requested: 'info',
  production_verified: 'ok',
}

/** 走到这两档，那个 SHA 已经不是「正在预览的这一版」，是「已经发出去的这一版」。 */
export const releasedStates: Record<DevelopmentTaskPreviewState, boolean> = {
  owner_tip: false,
  preview_ready: false,
  preview_running: false,
  test_candidate: false,
  preview_failed_rolled_back: false,
  release_requested: true,
  production_verified: true,
}

/**
 * 这次没交成，卡在谁那儿。
 *
 * 判据是 `code` 与 `kind`，不是状态码：403 与 404 都可能是「这个任务不归你」，
 * 而 409 与 400 是完全不同的两件事——一个是版本对不上（刷新就好），
 * 一个是这一版本身还没交清楚（等 AI）。混成一句「出错了」，人会去查错的地方。
 *
 * `missing` 是「后端压根没挂这条路由」，与 `capability_unavailable`（挂了但预览
 * 运行时还没接上）在用户那边是同一件事：都不是这一版代码的毛病，等和重试都没用。
 * 而 `unreachable` 是**请求根本没到后端**，修法在部署那一侧，不是同一个域——
 * `app/errors.ts` 把这两档分开正是为了这个，收在一句里等于把它又合回去。
 */
export function previewBlameCopy(cause: unknown): VarlessCopyId {
  if (!(cause instanceof ApiFailure)) return 'development_task.preview.blame.unknown'
  if (cause.kind === 'unreachable') return 'development_task.preview.blame.offline'
  if (cause.kind === 'missing') return 'development_task.preview.blame.platform'
  switch (cause.code) {
    case 'development_task.capability_unavailable':
      return 'development_task.preview.blame.platform'
    case 'development_task.conflict':
      return 'development_task.preview.blame.version'
    case 'invalid_request':
      return 'development_task.preview.blame.input'
    case 'auth.forbidden':
    case 'not_found':
      return 'development_task.preview.blame.access'
    default:
      return 'development_task.preview.blame.unknown'
  }
}

/**
 * 服务端会拒的那两档任务状态（`authorizeTestRecord` 里的 cancelled / failed）。
 *
 * 判据逐字照服务端写，**不复用 `isDevelopmentTaskFinal`**：那个把 `ready` 也算终态，
 * 而 `ready` 的任务恰恰是最该拿去看效果的那一版。借一个语义相近的判据过来，
 * 界面会拦下服务端本来收的东西，而且没有任何东西会告诉你。
 */
export function previewClosed(state: string): boolean {
  return state === 'cancelled' || state === 'failed'
}

/**
 * 按钮点不了的时候，是哪一种点不了。
 *
 * 三种的下一个动作完全不同：只能看的人得去找能动手的人，收摊的任务谁点都没用，
 * 还没交结果的等 AI 就行。合成一句「现在不能预览」，人会去试错的那件事。
 *
 * 返回 `null` = 现在能点。
 */
export function previewBlockedCopy(
  { canOperate, closed, hasTip }: { canOperate: boolean; closed: boolean; hasTip: boolean },
): VarlessCopyId | null {
  if (!canOperate) return 'development_task.preview.view_only'
  if (closed) return 'development_task.preview.closed'
  if (!hasTip) return 'development_task.preview.waiting'
  return null
}
